import { CheckableSelectItem, DropdownSelect } from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';

type EventTypeItem<T extends string> = {
  readonly id: T;
  readonly name: string;
};

export type EventTypeFilterProps<T extends string> = {
  /**
   * Event type id -> human-readable label; also defines the selectable options.
   * A label of `undefined` hides that event type from the list.
   */
  readonly labels: Readonly<Record<T, string | undefined>>;
  /** The currently selected event types. */
  readonly value: readonly T[];
  /** Called when the selected event types change. */
  readonly onChange: (eventTypes: T[]) => void;
};

/**
 * Generic event-type multi-select shared by the conversation- and user-resource
 * activity logs. Options are derived from `labels` and sorted alphabetically.
 */
export function EventTypeFilter<T extends string>({
  labels,
  value,
  onChange,
}: EventTypeFilterProps<T>): JSX.Element {
  const [searchTerm, setSearchTerm] = useState<string>();

  const allItems = useMemo<readonly EventTypeItem<T>[]>(
    () =>
      (Object.entries(labels) as [T, string | undefined][])
        // An `undefined` label hides that event type from the list.
        .flatMap(([id, name]) => (name === undefined ? [] : [{ id, name }]))
        .sort((first, second) => first.name.localeCompare(second.name)),
    [labels],
  );

  const items = useMemo<readonly EventTypeItem<T>[]>(() => {
    if (!searchTerm) {
      return allItems;
    }

    const term = searchTerm.toLowerCase();
    return allItems.filter(({ name }) => name.toLowerCase().includes(term));
  }, [allItems, searchTerm]);

  const handleChange = useCallback(
    (item: EventTypeItem<T>, checked: boolean) => {
      onChange(
        checked ? [...value, item.id] : value.filter((id) => id !== item.id),
      );
    },
    [onChange, value],
  );

  const handleClear = useCallback(() => onChange([]), [onChange]);

  const handleSearch = useCallback((search?: string) => {
    setSearchTerm(search?.trim() || undefined);
  }, []);

  const handleToggle = useCallback((isOpen: boolean) => {
    // Reset the search when the dropdown closes so it opens fresh next time.
    if (!isOpen) {
      setSearchTerm(undefined);
    }
  }, []);

  return (
    <DropdownSelect
      keepOpen
      hasSearch
      noArrow={false}
      items={items as EventTypeItem<T>[]}
      selectedItems={value as T[]}
      itemComponent={CheckableSelectItem}
      buttonProps={{ variant: 'tertiary', label: value.length || undefined }}
      dropdownProps={{ style: { minWidth: 280 } }}
      itemsDisplayCount={8}
      onChange={handleChange}
      onClear={handleClear}
      onSearch={handleSearch}
      onToggle={handleToggle}
    >
      Event types
    </DropdownSelect>
  );
}
